import { useHotkeys } from 'react-hotkeys-hook';
import { useAppDispatch, useAppSelector } from "../../../store";
import { outputActions } from "../../output/slice";
import { useRenderCache } from "../../renderer/hooks";
import { ResourceView } from "../../resources/components/ResourceView";
import { nextGroup, prevGroup, seek, seekByIndex } from '../../resources/functions';
import { useDroppable } from '@dnd-kit/core';
import { uiActions } from '../slice';
import { BlockType, LiturgyBlockType, ResourceType, SongBlockType } from '../../resources/types';
import { usePlaylistControls } from '../../playlist/hooks';

export const Live = () => {

  const liveResource = useAppSelector(state => state.ui.currentLiveResource);
  const render = useRenderCache();
  const rendered = render(liveResource);
  const liveAddress = useAppSelector(state => state.output.address);
  const dispatch = useAppDispatch();
  const playlistControls = usePlaylistControls();

  const { isOver, setNodeRef } = useDroppable({
    id: 'live',
  });

  const handleLineClick = (block: number) => (group: number) => (line: number) => {
    const address = { block, group, line, wasClicked: true };
    dispatch(outputActions.setBlockAddress(address));
  };

  const handleEditClick = () => {
    if (liveResource) {
      // Be sure to edit the _original_ resource from resources state, and not
      // the one from the render cache!
      dispatch(uiActions.setCurrentModal({
        modal: 'resourceEditor',
        payload: liveResource
      }));
    }
  };

  useHotkeys('down', () => {
    if (liveAddress && rendered) {
      dispatch(outputActions.setBlockAddress(
        nextGroup(rendered, liveAddress)
      ));
    }
  });

  useHotkeys('ctrl+left', playlistControls.back);
  useHotkeys('ctrl+right', playlistControls.forward);

  useHotkeys('up', () => {
    if (liveAddress && rendered) {
      dispatch(outputActions.setBlockAddress(prevGroup(rendered, liveAddress)));
    }
  });

  const seekNextBlock = (blockType: BlockType) => () => {
    if (liveAddress && rendered) {
      dispatch(outputActions.setBlockAddress(seek(rendered, liveAddress, blockType)));
    }
  };

  useHotkeys('v,V', seekNextBlock(SongBlockType.VERSE));
  useHotkeys('p,P', seekNextBlock(SongBlockType.PRECHORUS));
  useHotkeys('c,C', seekNextBlock(SongBlockType.CHORUS));
  useHotkeys('b,B', seekNextBlock(SongBlockType.BRIDGE));
  useHotkeys('t,T', seekNextBlock(SongBlockType.TAG));
  useHotkeys('r,R', seekNextBlock(SongBlockType.REFRAIN));
  useHotkeys('e,E', seekNextBlock(SongBlockType.ENDING));

  useHotkeys('1,2,3,4,5,6,7,8,9,0', (evt) => {
    if (rendered) {
      const targetIndex = parseInt(evt.key, 10) + (evt.key === '0' ? 10 : 0);
      const targetType = rendered?.type === ResourceType.SONG ? SongBlockType.VERSE : LiturgyBlockType.SECTION;
      const target = seekByIndex(rendered, targetType, targetIndex);
      if (target) {
        dispatch(outputActions.setBlockAddress(target));
      }
    }
  });

  return (
    <ResourceView
      className={ isOver ? '!bg-jw-blue' : undefined }
      liveAddress={liveAddress}
      onEdit={handleEditClick}
      onLineClick={handleLineClick}
      ref={setNodeRef}
      resource={rendered}
    />
  );
};
